import type { Invoice } from '../../types';

/**
 * Manifest der DATEV-Belegverwaltung online (Schema Document_v060.xsd).
 *
 * Ohne diese document.xml liegen die Beleg-PDFs im Belegordner nur herum: die
 * Kanzlei muss dann jedes Blatt von Hand an seine Buchung haengen. Mit dem
 * Manifest zieht DATEV die Zuordnung selbst, weil jedes <document> eine GUID
 * traegt, die im Buchungsstapel in der Spalte "Beleglink" als BEDI "<guid>"
 * wiederkehrt. Das Manifest ist also nichts weiter als die zweite Haelfte
 * dieser Klammer — und ohne sie ist die erste wertlos.
 *
 * Bewusst OHNE jszip, DOM, Supabase oder Browser-APIs: dieselbe Trennung, die
 * buildBelegZipEntries in datevExport.ts schon begruendet. Gezippt wird in der
 * Oberflaeche, gebaut und geprueft wird hier — und zwar ohne Testumgebung.
 */

// ═══════════════════════════════════════════════════════════════════════════
// GUID des Belegs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PRUEFEN: Belegtyp 2 = Ausgangsrechnung. Aus dem Referenzexport uebernommen,
 * nicht gegen die Schemadokumentation abgeglichen.
 */
const DOCUMENT_TYPE_AUSGANGSRECHNUNG = '2';

const UUID_PATTERN =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Die GUID eines Belegs im Manifest — das ist schlicht seine eigene id.
 *
 * invoices.id ist bereits eine echte UUID (supabase/invoices.sql: uuid primary
 * key default gen_random_uuid()). Sie zu hashen oder ueber UUIDv5 neu
 * abzuleiten waere eine zweite Identitaet fuer denselben Beleg, die auseinander
 * laufen kann, sobald jemand den Ableitungsweg anfasst.
 *
 * Das Format wird trotzdem geprueft, weil ein Verstoss sonst niemandem
 * auffiele: DATEV meldet eine kaputte GUID nicht als Fehler, es ordnet den
 * Beleg still nicht zu. Bemerkt wird das erst in der Kanzlei — an einer
 * Buchung ohne Beleg, Wochen spaeter, und dann sucht jemand lange. Lieber
 * hier laut scheitern.
 */
export const documentGuidForInvoice = (invoice: Pick<Invoice, 'id'>): string => {
    const id = (invoice.id ?? '').trim();
    if (!UUID_PATTERN.test(id)) {
        throw new Error(
            `Beleg-ID "${id || '—'}" ist keine gültige UUID und taugt nicht als ` +
                'DATEV-Beleg-GUID. DATEV würde den Beleg still nicht zuordnen, statt ' +
                'den Import abzulehnen.',
        );
    }
    return id;
};

// ═══════════════════════════════════════════════════════════════════════════
// Manifest
// ═══════════════════════════════════════════════════════════════════════════

export interface DatevDocumentEntry {
    /** GUID des Belegs; identisch mit dem Beleglink im Buchungsstapel. */
    guid: string;
    /** Dateiname des PDF im Belegordner, genau so wie er im ZIP liegt. */
    fileName: string;
}

/**
 * XML-Maskierung fuer Attributwerte.
 *
 * Die Dateinamen entstehen aus Rechnungsnummern, also aus freier
 * Benutzereingabe. Ein einziges & oder " darin macht das Manifest
 * unwohlgeformt, und DATEV lehnt dann nicht den einen Beleg ab, sondern den
 * kompletten Belegimport. Apostroph bleibt unmaskiert, weil alle Attribute
 * hier in doppelten Anfuehrungszeichen stehen.
 */
const escapeXmlAttribute = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

/** Zeitstempel des Manifests: JJJJ-MM-TTTHH:MM:SS, lokale Zeit ohne Zone — wie die Referenz. */
const formatManifestDate = (date: Date): string => {
    const pad = (value: number) => String(value).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

/**
 * Baut die document.xml des Belegordners.
 *
 * generatedAt ist Pflicht und wird nicht ersatzweise aus new Date() geholt —
 * dieselbe Begruendung wie bei DatevExportOptions.createdAt: ein Zeitstempel,
 * den der Aufrufer nicht setzen kann, macht jeden Test der Struktur unscharf.
 *
 * Doppelte GUIDs oder doppelte Dateinamen brechen den Lauf ab. Beides waere in
 * der Kanzlei nicht mehr aufloesbar: zwei Buchungen zeigten auf denselben
 * Beleg, oder zwei Manifesteintraege auf dieselbe Datei — und welcher der
 * richtige ist, steht nirgends.
 *
 * Eine leere Liste liefert ein gueltiges Manifest mit leerem <content>. Ob ein
 * Belegordner ohne Beleg ueberhaupt ein Manifest bekommt, entscheidet der
 * Aufrufer; diese Funktion hat dazu keine Meinung.
 */
export const buildDocumentXml = (entries: DatevDocumentEntry[], generatedAt: Date): string => {
    const seenGuids = new Set<string>();
    const seenFileNames = new Set<string>();

    for (const entry of entries) {
        if (seenGuids.has(entry.guid)) {
            throw new Error(
                `Beleg-GUID ${entry.guid} kommt im Manifest doppelt vor. Zwei Buchungen ` +
                    'auf denselben Beleg wären in der Buchhaltung nicht auflösbar.',
            );
        }
        seenGuids.add(entry.guid);

        if (seenFileNames.has(entry.fileName)) {
            throw new Error(
                `Belegdatei ${entry.fileName} kommt im Manifest doppelt vor. Im ` +
                    'Belegordner kann es sie nur einmal geben.',
            );
        }
        seenFileNames.add(entry.fileName);
    }

    const documents = entries
        .map(
            (entry) =>
                `<document guid="${escapeXmlAttribute(entry.guid)}" ` +
                `type="${DOCUMENT_TYPE_AUSGANGSRECHNUNG}">` +
                `<extension name="${escapeXmlAttribute(entry.fileName)}" xsi:type="File"/>` +
                '</document>',
        )
        .join('');

    // Reihenfolge und Schreibweise der Attribute folgen dem von der Kanzlei
    // akzeptierten Referenzexport. Abweichend ist allein generatingSystem —
    // das Manifest kommt aus OS-IA und soll sich nicht als fremdes System
    // ausgeben.
    return (
        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
        '<archive xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ' +
        'generatingSystem="OS-IA" version="6.0" ' +
        'xsi:schemaLocation="http://xml.datev.de/bedi/tps/document/v06.0 Document_v060.xsd" ' +
        'xmlns="http://xml.datev.de/bedi/tps/document/v06.0">' +
        `<header><date>${formatManifestDate(generatedAt)}</date></header>` +
        `<content>${documents}</content>` +
        '</archive>'
    );
};
